import { TableColumnsType } from 'antd';
import { LayoutOutlined, WhatsAppOutlined, SecurityScanOutlined, LockOutlined, FileTextOutlined, SafetyOutlined } from '@ant-design/icons';
import { AppearanceState, AssistantTableData, WidgetDataItem } from './types';
import { ReactNode } from 'react';

// Import icons
import stripeIcon from '../../assets/icons/stripe.png';
import zendeskIcon from '../../assets/icons/zendesk.png';
import slackIcon from '../../assets/icons/slack.png';
import salesforceIcon from '../../assets/icons/salesforce.png';
import jiraIcon from '../../assets/icons/jira.png';
import pdfIcon from '../../assets/icons/pdf.png';
import gmailIcon from '../../assets/icons/gmail.svg';
import hubspotIcon from '../../assets/icons/hubspot.png';
import excelIcon from '../../assets/icons/excel.svg';
import sheetsIcon from '../../assets/icons/sheets.svg';
import openaiIcon from '../../assets/icons/openai.svg';
import n8nIcon from '../../assets/icons/n8n.webp';
import outlookIcon from '../../assets/icons/outlook.svg';

export const WIDGET_DATA: WidgetDataItem[] = [
  {
    iconType: 'layout',
    iconStyle: {
      fontSize: '24px',
      color: 'var(--ant-colorIcon)',
      background: 'var(--ant-colorAvatar)',
      padding: '12px',
      borderRadius: '8px'
    },
    title: 'Website Widget',
    description: 'Embed the virtual agent on your website',
    setup: 'Easy',
    action: 'Configure',
  },
  {
    iconType: 'whatsapp',
    iconStyle: {
      fontSize: '24px',
      color: '#ffffff',
      background: '#25D366',
      padding: '12px',
      borderRadius: '8px'
    },
    title: 'Whatsapp',
    description: 'Connect with your customers through WhatsApp Business',
    setup: 'Medium',
    action: 'Configure',
  },
];

export const columns: TableColumnsType<AssistantTableData> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Active State',
      dataIndex: 'activeState',
      key: 'activeState',
    },
    {
      title: 'Schedule',
      dataIndex: 'schedule',
      key: 'schedule',
    },
    {
      title: '',
      key: 'manageSchedule',
    },
    {
      title: 'Last Sync',
      dataIndex: 'lastSync',
      key: 'lastSync',
    },
    {
      title: 'Action',
      key: 'action',
    },
  ];


export const assistantLayoutTabItems = [
  {
    key: 'personality',
    label: 'Personality',
    children:
      'Set up the identity and communication style of your virtual agent. Define its name, role, main topics, and interaction style.',
  },
  {
    key: 'flows',
    label: 'Flows & Integrations',
    children:
      'Configure the interaction flows of the virtual agent. Define how the agent will respond to user inputs, the decision it will make, and the possible conversation routes.',
  },
  {
    key: 'knowledge',
    label: 'Knowledge',
    children:
      "Configure your virtual agent's training sources. Add and manage key information, such as FAQs, documents, and URLs, to ensure your agent is equipped with accurate and relevant knowledge for seamless customer interactions.",
  },
  {
    key: 'channels',
    label: 'Channels',
    children:
      "Configure your virtual agent's integration channels. Define the necessary technical parameters, to connect your agent with various communication platforms.",
  },
  {
    key: 'appearance',
    label: 'Appearance',
    children: 'Customize the appearance of your virtual agent to align with your brand',
  },
];

// Advanced Configuration Constants
export const DEFAULT_GUARDRAILS = [
  // ⚡ Fast pattern-based guards first
  {
    id: 'piiGuard',
    name: 'PII Protection',
    description: 'Detects and blocks personally identifiable information like names, emails, phone numbers, and addresses',
    category: 'privacy' as const,
    enabled: true,
    requiresLLM: false,
  },
  {
    id: 'secretGuard',
    name: 'Secret Detection',
    description: 'Identifies potential secrets, API keys, passwords, and sensitive credentials',
    category: 'security' as const,
    enabled: true,
    requiresLLM: false,
  },
  // 🐌 LLM-based guards last
  {
    id: 'profanityGuard',
    name: 'Profanity Filter',
    description: 'Detects and filters inappropriate or offensive language',
    category: 'content' as const,
    enabled: true,
    requiresLLM: true,
  },
  {
    id: 'toxicGuard',
    name: 'Toxic Content Detection',
    description: 'Identifies harmful, threatening, or dangerous content',
    category: 'content' as const,
    enabled: true,
    requiresLLM: true,
  },
  {
    id: 'hateSpeechGuard',
    name: 'Hate Speech Detection',
    description: 'Detects discriminatory language and hate speech',
    category: 'content' as const,
    enabled: true,
    requiresLLM: true,
  },
  {
    id: 'adultContentGuard',
    name: 'Adult Content Filter',
    description: 'Filters not-safe-for-work (NSFW) content',
    category: 'content' as const,
    enabled: true,
    requiresLLM: true,
  },
  {
    id: 'biasDetectionGuard',
    name: 'Bias Detection',
    description: 'Identifies potential biases in responses and content',
    category: 'ethics' as const,
    enabled: true,
    requiresLLM: true,
  },
  {
    id: 'copyrightGuard',
    name: 'Copyright Detection',
    description: 'Detects potential copyright violations and plagiarism',
    category: 'ethics' as const,
    enabled: true,
    requiresLLM: true,
  },
  {
    id: 'injectionGuard',
    name: 'Prompt Injection Protection',
    description: 'Blocks attempts to manipulate the AI through prompt injection attacks',
    category: 'security' as const,
    enabled: true,
    requiresLLM: true,
  },
  {
    id: 'leakageGuard',
    name: 'System Prompt Protection',
    description: 'Prevents extraction of internal system prompts and configurations',
    category: 'security' as const,
    enabled: true,
    requiresLLM: true,
  },
];

export const GUARDRAIL_PRESETS = [
  {
    id: 'Minimal (Speed)',
    name: 'Concise',
    description: 'Essential guards without LLM for maximum speed',
    config: DEFAULT_GUARDRAILS.filter(guard => !guard.requiresLLM),
  },
  {
    id: 'balanced',
    name: 'Balanced',
    description: 'Mix of critical guards for balance between speed and security',
    config: DEFAULT_GUARDRAILS.filter(guard => 
      guard.category === 'security' || 
      guard.category === 'privacy' || 
      (guard.category === 'content' && ['profanityGuard', 'toxicGuard'].includes(guard.id))
    ),
  },
  {
    id: 'maximum',
    name: 'Maximum (Security)',
    description: 'All guards active for maximum protection',
    config: DEFAULT_GUARDRAILS,
  },
];

export const DEFAULT_BUSINESS_RULES = [
  {
    id: 'medical-disclaimer',
    rule: 'No proporcionar información médica específica sin disclaimer',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'data-confirmation',
    rule: 'Siempre confirmar datos sensibles antes de procesarlos',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export const GUARDRAIL_CATEGORIES = {
  security: { label: 'Security', color: 'transparent', icon: SecurityScanOutlined },
  privacy: { label: 'Privacy', color: 'transparent', icon: LockOutlined },
  content: { label: 'Content', color: 'transparent', icon: FileTextOutlined },
  ethics: { label: 'Ethics', color: 'transparent', icon: SafetyOutlined },
};

export const DEFAULT_APPEARANCE_STATE: AppearanceState = {
  primaryColor: '#E3F571',
  headerColor: '#7E70B3',
  greetingMessage: 'Welcome to ECA.',
  iconColor: '#000000'
};

export const INBOX_MESSAGES = [
  { message: 'Hi, my payment was declined, but...', time: '2d ago' },
  { message: 'Hello, could you please help me wi...', time: '3d ago' },
  { message: 'I need your help', time: '3d ago' },
];

export const PERSONALITY_COLORS = {
  defaultColor: '#000000',
  iconActiveColor: '#2BA5B8',
} as const;

export const INTEGRATION_ICONS = {
  'stripe': stripeIcon,
  'zendesk': zendeskIcon,
  'slack': slackIcon,
  'salesforce': salesforceIcon,
  'jira': jiraIcon,
  'pdf': pdfIcon,
  'gmail': gmailIcon,
  'hubspot': hubspotIcon,
  'excell': excelIcon,
  'sheets': sheetsIcon,
  'openai': openaiIcon,
  'n8n': n8nIcon,
  'outlook': outlookIcon,
} as const;

// LiteLLM Model Options for agent configuration
// These model aliases match the names defined in infrastructure/litellm/config.yaml
export const MODEL_OPTIONS = [
  {
    label: 'Default',
    options: [
      { value: 'gpt-4o', label: 'Default (GPT-4o)' },
    ],
  },
  {
    label: 'OpenAI',
    options: [
      { value: 'gpt-5.4', label: 'GPT-5.4 (Flagship)' },
      { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
      { value: 'gpt-5-nano', label: 'GPT-5 Nano' },
      { value: 'o3-mini', label: 'O3 Mini (Reasoning)' },
    ],
  },
  {
    label: 'Anthropic',
    options: [
      { value: 'claude-opus-4.6', label: 'Claude Opus 4.6' },
      { value: 'claude-sonnet-4.6', label: 'Claude Sonnet 4.6' },
      { value: 'claude-haiku-4.5', label: 'Claude Haiku 4.5' },
    ],
  },
  {
    label: 'Google Gemini',
    options: [
      { value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro' },
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
      { value: 'gemini-3-flash', label: 'Gemini 3 Flash' },
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
      { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite' },
    ],
  },
  {
    label: 'AWS Bedrock (No API Key needed)',
    options: [
      { value: 'bedrock-claude-sonnet-4.6', label: 'Bedrock Claude Sonnet 4.6' },
      { value: 'bedrock-claude-haiku-4.5', label: 'Bedrock Claude Haiku 4.5' },
    ],
  },
];

// WhatsApp communication mode options for agent configuration
export const WHATSAPP_MODE_OPTIONS = [
  { value: 'direct', label: 'Direct Messages' },
  { value: 'groups', label: 'WhatsApp Groups' },
  { value: 'both', label: 'Both (Direct + Groups)' },
];

// Models that don't require an API key (Bedrock uses IAM from the EKS node role)
export const MODELS_WITHOUT_API_KEY = [
  'bedrock-claude-sonnet-4.6',
  'bedrock-claude-haiku-4.5',
  'bedrock-claude-sonnet',
  'bedrock-claude-haiku',
];

export const STATUS_FILTERS = ['All', 'Active', 'Inactive'] as const;

export const SORT_OPTIONS = [
  { value: 'Most Recent', label: 'Sort by: Most Recent' },
  { value: 'Oldest', label: 'Sort by: Oldest' }
];